import React, { useState } from 'react';
import { scrollToTop } from '../../utils/scrollUtils';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DynamicInfographic from '../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../components/navigation/ModuleSidebar';

const UnderstandingFallRisks = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('1.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});  // To track if answers are correct or incorrect
  
  const handleBack = () => {
    navigate('/courses/fall-prevention');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '1.1-1': 2, // High blood pressure (NOT a common physical factor)
    '1.1-2': 1, // Grab bars near toilet and shower
    '1.1-3': 0, // Lower body strength
    '1.2-1': 1, // Dementia
    '1.2-2': 1, // Previous fall history
    '1.2-3': 1, // Parkinson's disease
    '1.3-1': 1, // Sedatives and sleeping pills
    '1.3-2': 0, // Removing throw rugs
    '1.3-3': 1, // Removal of environmental hazards
    '1.4-1': 1, // Self-imposed activity restriction
    '1.4-2': 2, // Increased confidence in mobility
    '1.4-3': 1, // Fear of falling
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = correctAnswers[key] === answerIndex;
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Define all sections for the module
  const sections = [
    {
      id: '1.1',
      title: '1.1 Common Causes of Falls',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 1, color: '#e57373' }} /> 1.1 Common Causes of Falls
          </Typography>
          
          <Typography paragraph>
            Falls are rarely caused by a single factor. Instead, they typically result from a complex interaction 
            of multiple risk factors. Understanding these common causes is the first step in effective prevention.
          </Typography>
          
          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic module="UnderstandingFallRisks" topic="Fall Risk Factors" />

          <Box sx={{ bgcolor: 'rgba(229, 115, 115, 0.1)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Key Statistics</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                    <ListItemText primary="Falls are the leading cause of injury-related deaths in adults over 65" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                    <ListItemText primary="One in four older adults falls each year" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                    <ListItemText primary="Every 11 seconds, an older adult is treated in the emergency room for a fall" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                    <ListItemText primary="Falls result in more than 3 million emergency department visits annually" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ bgcolor: '#e57373', color: 'white', p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>25%</Typography>
                  <Typography variant="body2">of adults 65+ fall each year</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>3M+</Typography>
                  <Typography variant="body2">emergency department visits annually</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Physical Factors</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Muscle weakness, particularly in the lower extremities" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Gait and balance problems" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Visual impairments" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Postural hypotension (blood pressure drop when standing)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Chronic conditions affecting mobility or balance" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Environmental Factors</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Poor lighting conditions" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Slippery floors or loose rugs" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Lack of handrails or grab bars" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Cluttered walkways and obstacles" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Uneven surfaces or stairs" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 1.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is NOT a common physical factor contributing to falls?
                    </Typography>
                    <List dense>
                      {['Muscle weakness in lower extremities', 'Visual impairments', 'High blood pressure'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.1-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.1-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.1-1'] && index === correctAnswers['1.1-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.1', '1', index)}
                            disabled={answerStatus['1.1-1'] === true && index !== correctAnswers['1.1-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.1-1'] === true && index !== correctAnswers['1.1-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.1-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          High blood pressure is not directly a physical factor contributing to falls. Muscle weakness and visual impairments are significant physical risk factors for falls.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which environmental modification is most effective for preventing bathroom falls?
                    </Typography>
                    <List dense>
                      {['Carpeted floors', 'Grab bars near toilet and shower', 'Bright overhead lighting'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.1-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.1-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.1-2'] && index === correctAnswers['1.1-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.1', '2', index)}
                            disabled={answerStatus['1.1-2'] === true && index !== correctAnswers['1.1-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.1-2'] === true && index !== correctAnswers['1.1-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.1-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Grab bars near toilets and showers provide crucial support for balance when transferring and during bathing activities, significantly reducing fall risk in bathrooms.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which physical factor is most important to address for fall prevention?
                    </Typography>
                    <List dense>
                      {['Lower body strength', 'Upper body flexibility', 'Cardiovascular endurance'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.1-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.1-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.1-3'] && index === correctAnswers['1.1-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.1', '3', index)}
                            disabled={answerStatus['1.1-3'] === true && index !== correctAnswers['1.1-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.1-3'] === true && index !== correctAnswers['1.1-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.1-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Lower body strength, particularly in the legs and core, is crucial for maintaining balance and stability. Strengthening these muscle groups significantly reduces fall risk by improving posture and gait.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '1.2',
      title: '1.2 High-Risk Individuals and Conditions',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#e57373' }} /> 1.2 High-Risk Individuals and Conditions
          </Typography>
          
          <Typography paragraph>
            Certain individuals are at higher risk for falls due to age, medical conditions, or other factors. 
            Identifying these high-risk individuals allows for more targeted prevention strategies.
          </Typography>
          
          {/* High-Risk Individuals Infographic */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373' }}>High-Risk Individuals Profile</Typography>
            <Typography variant="body2" paragraph>
              Understanding which individuals are at highest risk for falls allows caregivers to provide targeted preventive care and monitoring.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Understanding High-Risk Conditions</Typography>
                  <Typography paragraph variant="body2">
                    Certain medical conditions significantly increase fall risk due to their effects on balance, mobility, and cognition. Understanding the mechanisms behind this increased risk is essential for caregivers.
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Neurological Conditions" 
                        secondary="Conditions affecting the nervous system often impact balance, coordination, and muscle control"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Musculoskeletal Conditions" 
                        secondary="Affect strength, flexibility, and joint function, altering gait patterns"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Cognitive Conditions" 
                        secondary="Impact awareness, judgment, and the ability to recognize and respond to hazards"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Sensory Impairments" 
                        secondary="Reduce environmental awareness and the ability to detect obstacles or changes in terrain"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WarningIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Stroke" 
                        secondary="May result in one-sided weakness, balance issues, and visual field deficits"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>High-Risk Demographics</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><PersonIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Adults over 65" 
                        secondary="Risk increases with age due to multiple factors including decreased muscle mass and sensory changes"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Previous Fall History" 
                        secondary="Those who have fallen before are at significantly higher risk of falling again"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Polypharmacy Patients" 
                        secondary="Taking multiple medications increases risk due to side effects and interactions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Recent Hospital Discharge" 
                        secondary="Deconditioning, medication changes, and new environments increase risk"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PersonIcon sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Individuals with Assistive Devices" 
                        secondary="May indicate mobility issues and require proper training for safe use"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Condition-Specific Risk Factors Summary */}
          <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373' }}>Understanding Condition-Specific Risk Factors</Typography>
            <Typography variant="body2" paragraph>
              Different medical conditions present unique fall risk profiles. Recognizing these patterns helps caregivers anticipate and prevent falls more effectively.
            </Typography>
          </Box>

          {/* Quiz for Section 1.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following conditions is associated with impaired judgment and spatial awareness?
                    </Typography>
                    <List dense>
                      {['Arthritis', 'Dementia', 'Diabetes'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.2-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.2-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.2-1'] && index === correctAnswers['1.2-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.2', '1', index)}
                            disabled={answerStatus['1.2-1'] === true && index !== correctAnswers['1.2-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.2-1'] === true && index !== correctAnswers['1.2-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.2-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Dementia is associated with impaired judgment and spatial awareness. These cognitive deficits significantly increase fall risk as individuals may not recognize hazards or may misjudge distances and spatial relationships.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which demographic factor significantly increases fall risk?
                    </Typography>
                    <List dense>
                      {['Being under 50 years old', 'Previous fall history', 'Living in a single-story home'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.2-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.2-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.2-2'] && index === correctAnswers['1.2-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.2', '2', index)}
                            disabled={answerStatus['1.2-2'] === true && index !== correctAnswers['1.2-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.2-2'] === true && index !== correctAnswers['1.2-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.2-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Previous fall history is a significant risk factor for future falls. Research shows that individuals who have fallen before are at much higher risk of falling again, often due to underlying physical issues, fear of falling, or environmental hazards that haven't been addressed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of these is a neurological condition that increases fall risk?
                    </Typography>
                    <List dense>
                      {['Hypertension', 'Parkinson\'s disease', 'Osteoporosis'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.2-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.2-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.2-3'] && index === correctAnswers['1.2-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.2', '3', index)}
                            disabled={answerStatus['1.2-3'] === true && index !== correctAnswers['1.2-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.2-3'] === true && index !== correctAnswers['1.2-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.2-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Parkinson's disease is a neurological condition that affects movement, causing tremors, rigidity, and balance problems. These symptoms significantly increase fall risk as they directly impact mobility and stability.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '1.3',
      title: '1.3 Physical, Environmental, and Medication-Related Risks',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <MedicalServicesIcon sx={{ mr: 1, color: '#e57373' }} /> 1.3 Physical, Environmental, and Medication-Related Risks
          </Typography>
          
          <Typography paragraph>
            Fall risk is often categorized into physical, environmental, and medication-related factors. 
            Understanding these categories helps in developing comprehensive prevention strategies.
          </Typography>
          
          {/* Risk Categories Overview */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373' }}>Understanding Risk Categories</Typography>
            <Typography variant="body2" paragraph>
              Fall risks can be categorized into physical, environmental, and medication-related factors. Each category requires different prevention approaches.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
                    Physical Risk Factors
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Muscle Weakness" 
                        secondary="Particularly in lower extremities, affecting stability and mobility"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Balance Disorders" 
                        secondary="Including vestibular issues and proprioception deficits"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Gait Abnormalities" 
                        secondary="Shuffling, asymmetry, or reduced step length"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Visual Impairments" 
                        secondary="Reduced depth perception, contrast sensitivity, or visual fields"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Foot Problems" 
                        secondary="Pain, deformities, or inappropriate footwear"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
                    Environmental Risk Factors
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Poor Lighting" 
                        secondary="Especially in stairwells, hallways, and bathrooms"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Tripping Hazards" 
                        secondary="Loose rugs, clutter, cords, or uneven surfaces"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Lack of Safety Equipment" 
                        secondary="Missing handrails, grab bars, or non-slip mats"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Inappropriate Furniture" 
                        secondary="Too low, unstable, or difficult to get in/out of"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Weather Conditions" 
                        secondary="Ice, snow, rain, or extreme temperatures"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
                    Medication-Related Risk Factors
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Psychotropic Medications" 
                        secondary="Sedatives, antipsychotics, and antidepressants"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Cardiovascular Medications" 
                        secondary="Diuretics, antihypertensives, and cardiac glycosides"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Polypharmacy" 
                        secondary="Taking multiple medications increases risk exponentially"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Recent Medication Changes" 
                        secondary="New medications or dosage adjustments"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Medication Side Effects" 
                        secondary="Dizziness, orthostatic hypotension, or drowsiness"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 1.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which medication class is MOST likely to increase fall risk?
                    </Typography>
                    <List dense>
                      {['Antibiotics', 'Sedatives and sleeping pills', 'Vitamins'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.3-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.3-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.3-1'] && index === correctAnswers['1.3-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.3', '1', index)}
                            disabled={answerStatus['1.3-1'] === true && index !== correctAnswers['1.3-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.3-1'] === true && index !== correctAnswers['1.3-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.3-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Sedatives and sleeping pills can cause drowsiness, dizziness, and impaired coordination, significantly increasing fall risk. They affect balance and reaction time, making individuals more susceptible to falls, especially at night.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which environmental modification is most effective for preventing falls?
                    </Typography>
                    <List dense>
                      {['Removing throw rugs', 'Installing thick carpet', 'Using dim lighting to reduce glare'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.3-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.3-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.3-2'] && index === correctAnswers['1.3-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.3', '2', index)}
                            disabled={answerStatus['1.3-2'] === true && index !== correctAnswers['1.3-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.3-2'] === true && index !== correctAnswers['1.3-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.3-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Removing throw rugs eliminates a major tripping hazard in the home. Unsecured rugs and mats are one of the most common causes of falls, especially for individuals with mobility challenges or who use assistive devices.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which physical modification has the greatest impact on reducing fall risk?
                    </Typography>
                    <List dense>
                      {['Improved lighting in hallways', 'Removal of environmental hazards', 'Decorative wall hangings'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.3-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.3-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.3-3'] && index === correctAnswers['1.3-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.3', '3', index)}
                            disabled={answerStatus['1.3-3'] === true && index !== correctAnswers['1.3-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.3-3'] === true && index !== correctAnswers['1.3-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.3-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Removal of environmental hazards such as clutter, loose cords, and unstable furniture significantly reduces fall risk by eliminating common tripping hazards. This is one of the most effective and immediate interventions for fall prevention.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '1.4',
      title: '1.4 Psychological and Emotional Impact of Falls',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <PsychologyIcon sx={{ mr: 1, color: '#e57373' }} /> 1.4 Psychological and Emotional Impact of Falls
          </Typography>
          
          <Typography paragraph>
            Beyond physical injuries, falls can have significant psychological and emotional consequences. 
            Understanding these impacts is crucial for comprehensive care and prevention.
          </Typography>
          
          {/* Psychological Impact Overview */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373' }}>Psychological Impact Overview</Typography>
            <Typography variant="body2" paragraph>
              The psychological consequences of falls can be as significant as physical injuries. Understanding these impacts helps caregivers provide holistic support.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
                    Fear of Falling
                  </Typography>
                  <Typography paragraph>
                    Fear of falling is a common psychological consequence that can develop even in individuals who haven't experienced a fall. 
                    This fear can lead to self-imposed activity restriction, which paradoxically increases fall risk by contributing to:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Reduced physical activity and deconditioning" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Decreased muscle strength and balance" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Social isolation and withdrawal" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Loss of confidence and independence" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Increased anxiety and depression" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
                    Post-Fall Syndrome
                  </Typography>
                  <Typography paragraph>
                    Post-fall syndrome refers to the psychological consequences that can occur after experiencing a fall, 
                    particularly one that resulted in injury or a long lie (being unable to get up for an extended period).
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Intense fear and anxiety about falling again" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Loss of self-efficacy and confidence in mobility" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Depression and feelings of helplessness" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="PTSD-like symptoms when faced with similar situations" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#e57373' }} /></ListItemIcon>
                      <ListItemText primary="Increased dependency and reluctance to perform ADLs" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 1.4 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is fear of falling most likely to lead to?
                    </Typography>
                    <List dense>
                      {['Increased physical activity', 'Self-imposed activity restriction', 'Improved balance'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.4-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.4-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.4-1'] && index === correctAnswers['1.4-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.4', '1', index)}
                            disabled={answerStatus['1.4-1'] === true && index !== correctAnswers['1.4-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.4-1'] === true && index !== correctAnswers['1.4-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.4-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Fear of falling often leads to self-imposed activity restriction as individuals avoid activities they perceive as risky. This can create a cycle where reduced activity leads to muscle weakness and deconditioning, which further increases fall risk.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is NOT a symptom of post-fall syndrome?
                    </Typography>
                    <List dense>
                      {['Intense fear of falling again', 'Depression and feelings of helplessness', 'Increased confidence in mobility'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.4-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.4-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.4-2'] && index === correctAnswers['1.4-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.4', '2', index)}
                            disabled={answerStatus['1.4-2'] === true && index !== correctAnswers['1.4-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.4-2'] === true && index !== correctAnswers['1.4-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.4-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Increased confidence in mobility is NOT a symptom of post-fall syndrome. In fact, post-fall syndrome typically involves decreased confidence and self-efficacy in mobility. Individuals often experience fear, anxiety, and loss of confidence in their ability to move safely.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which psychological consequence can develop even in individuals who haven't experienced a fall?
                    </Typography>
                    <List dense>
                      {['Post-fall syndrome', 'Fear of falling', 'PTSD-like symptoms'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['1.4-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['1.4-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['1.4-3'] && index === correctAnswers['1.4-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('1.4', '3', index)}
                            disabled={answerStatus['1.4-3'] === true && index !== correctAnswers['1.4-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['1.4-3'] === true && index !== correctAnswers['1.4-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['1.4-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(229, 115, 115, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Fear of falling can develop in individuals who have never experienced a fall. It may arise from witnessing others fall, hearing about falls, or simply from awareness of personal risk factors and declining physical abilities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        variant="outlined" 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 0, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3, bgcolor: 'rgba(229, 115, 115, 0.05)' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#e57373', fontWeight: 600 }}>
            Module 1: Understanding Fall Risks
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            This module covers common causes of falls, high-risk individuals and conditions, physical, environmental, 
            and medication-related risks, and the psychological impact of falls.
          </Typography>
        </Box>
        
        <Divider />
        
        <Grid container>
          {/* Sidebar */}
          <Grid item xs={12} md={3} sx={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <ModuleSidebar 
              sections={sections.map(section => ({ id: section.id, title: section.title }))}
              activeSection={activeSection}
              onSectionChange={(sectionId) => {
                setActiveSection(sectionId);
                scrollToTop();
              }}
              moduleTitle="Module 1: Understanding Fall Risks"
            />
          </Grid>
          
          {/* Content */}
          <Grid item xs={12} md={9}>
            <Box sx={{ p: 4 }}>
              {sections.find(section => section.id === activeSection)?.content}
              
              {/* Module Buttons */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="outlined" 
                  startIcon={<ArrowBackIcon />} 
                  onClick={handleBack}
                >
                  Back to Learning Modules
                </Button>
                {activeSection !== sections[sections.length - 1].id && (
                  <Button 
                    variant="contained" 
                    endIcon={<ArrowForwardIcon />} 
                    onClick={handleNextSection}
                    sx={{ bgcolor: '#e57373', '&:hover': { bgcolor: '#ef5350' } }}
                  >
                    Next Section
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default UnderstandingFallRisks;